import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { useLang } from "../context/LanguageContext";
import { useSiteContent } from "../hooks/useSiteContent";

function KineticHero() {
  const { t } = useLang();
  const { txt, img, num } = useSiteContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const lines = [txt("hero_l1", "hero.l1"), txt("hero_l2", "hero.l2"), txt("hero_l3", "hero.l3")];
  const sizeScale = num("hero_title_size", 100) / 100;
  const heroImg = img("hero_image", "https://images.pexels.com/photos/8024762/pexels-photo-8024762.jpeg");

  return (
    <section ref={ref} className="relative h-[100svh] flex items-end overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={heroImg} alt="Operación minera" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-inverse/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-inverse via-inverse/20 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 md:px-10 pb-16 md:pb-24 text-[color:var(--bg)]">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }}
          className="overline text-gold mb-6">{txt("hero_overline", "hero.overline")}</motion.p>

        <h1 className="font-display font-extrabold tracking-tighter leading-[0.92]"
          style={{ fontSize: `clamp(${2.6 * sizeScale}rem, ${8 * sizeScale}vw, ${7 * sizeScale}rem)` }}>
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span className="block"
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ delay: 0.2 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
          <Link to="/catalogo" data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-gold text-inverse px-8 py-4 font-medium hover:bg-[color:var(--bg)] hover:text-inverse transition-colors duration-300">
            {txt("hero_cta1", "hero.cta1")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/consultoria" className="inline-flex items-center gap-2 px-6 py-4 border border-[color:var(--bg)]/30 hover:border-gold hover:text-gold transition-colors duration-300">
            {txt("hero_cta2", "hero.cta2")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

const MARQUEE = ["Extracción", "Processing", "Geología", "Automation", "ESG", "Logística", "Telemetry", "Optimización"];

function Marquee() {
  return (
    <div className="py-6 bg-blue text-[color:var(--bg)] overflow-hidden border-y border-gold/20" data-testid="marquee">
      <div className="animate-marquee">
        {[0, 1].map((r) => (
          <div key={r} className="flex items-center">
            {MARQUEE.map((m, i) => (
              <span key={i} className="flex items-center font-display font-light text-2xl md:text-3xl px-8">
                {m} <span className="text-gold ml-8">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Manifesto() {
  const { t } = useLang();
  const items = [
    { n: "01", t: t("m1t"), d: t("m1d") },
    { n: "02", t: t("m2t"), d: t("m2d") },
    { n: "03", t: t("m3t"), d: t("m3d") },
  ];
  return (
    <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-28 md:py-40" data-testid="manifesto">
      <Reveal className="max-w-2xl mb-20">
        <p className="overline text-gold mb-5">{t("manifesto.overline")}</p>
        <h2 className="font-display font-extrabold tracking-tighter text-[clamp(2rem,5vw,4rem)] leading-[0.95]">{t("manifesto.title")}</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-px bg-ink/10">
        {items.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.12} className="bg-[color:var(--bg)] p-8 md:p-10 group">
            <span className="font-mono text-gold text-sm">{m.n}</span>
            <h3 className="font-display font-extrabold text-3xl mt-8 mb-4 group-hover:translate-x-1 transition-transform duration-300">{m.t}</h3>
            <p className="text-ink/60 leading-relaxed">{m.d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  const { txt } = useSiteContent();
  const [featured, setFeatured] = useState([]);
  useEffect(() => { api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data)); }, []);

  return (
    <div>
      <KineticHero />
      <Marquee />
      <Manifesto />

      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-28 md:pb-40" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="overline text-gold mb-4">{t("featured.overline")}</p>
            <h2 className="font-display font-extrabold tracking-tighter text-[clamp(1.8rem,4vw,3.2rem)] leading-none">{t("featured.title")}</h2>
          </div>
          <Link to="/catalogo" className="group inline-flex items-center gap-2 font-medium hover:text-gold transition-colors">
            {t("featured.viewAll")} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      <section className="relative overflow-hidden bg-inverse text-[color:var(--bg)] grain" data-testid="cta-band">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-28 md:py-36 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="overline text-gold mb-5">{t("cta.overline")}</p>
            <h2 className="font-display font-extrabold tracking-tighter text-[clamp(2rem,5vw,4rem)] leading-[0.95]">{txt("cta_title", "cta.title")}</h2>
          </Reveal>
          <Reveal delay={0.15} className="md:justify-self-end">
            <p className="text-[color:var(--bg)]/60 mb-8 max-w-md leading-relaxed">{txt("cta_desc", "cta.desc")}</p>
            <Link to="/consultoria" className="inline-flex items-center gap-3 bg-gold text-inverse px-8 py-4 font-medium hover:bg-[color:var(--bg)] transition-colors duration-300 group">
              {t("cta.btn")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
